import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { AppStateFacade } from '@app-state';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    IMachine,
    ModalResultType,
} from '@models';
import { MachineService } from '@services';
import { unsubscribe } from '@utils/object.utils';
import UiValidators from '@validators';

import {
    BehaviorSubject,
    Observable,
    Subscription,
} from 'rxjs';
import {
    finalize,
    map,
} from 'rxjs/operators';

interface LicenseInfo {
    available: number;
    total: number;
}

@Component({
    templateUrl: './runtime-edit.modal.html',
    styleUrls: ['./runtime-edit.modal.scss'],
    providers: [MachineService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RuntimeEditModalComponent
    extends UiModalComponent<Partial<IMachine>>
    implements AfterViewInit, OnInit, OnDestroy {
    public form!: FormGroup;
    public licenseInfo$!: Observable<LicenseInfo>;
    public loading$ = new BehaviorSubject(false);

    private _validatorSub?: Subscription;
    private _machineService = this._injector.get<MachineService>(MachineService);
    private _appState = this._injector.get<AppStateFacade>(AppStateFacade);

    ngOnInit() {
        this.form = this._injector.get<FormBuilder>(FormBuilder).group({
            'runtimes': [this.context.data.nonProductionSlots],
        });

        this.licenseInfo$ = this._appState.user.license.value$
            .pipe(
                map(license => {
                    const licenseKey = this.context.additionalData === 'Unattended' ?
                        'unattended' :
                        'nonProduction';
                    return {
                        available: Math.max(0, license!.allowed[licenseKey] - license!.used[licenseKey]),
                        total: license!.allowed[licenseKey],
                    };
                }),
            );
    }

    ngAfterViewInit() {
        // set the max dynamically
        this._validatorSub = this.licenseInfo$.subscribe(licenseInfo => {
            this.form.get('runtimes')!.setValidators([
                Validators.required,
                UiValidators.range(1, licenseInfo.total),
            ]);
            this.form.get('runtimes')!.updateValueAndValidity();
            this._cd.detectChanges();
        });
    }

    ngOnDestroy() {
        unsubscribe(
            this._validatorSub,
        );
    }

    public onSubmit() {
        if (
            this.form.invalid ||
            this.loading$.value
        ) { return; }

        this.loading$.next(true);

        const value = Number.parseInt(this.form.get('runtimes')!.value, 10);
        const payload = {} as IMachine;

        if (this.context.additionalData === 'Unattended') {
            payload.unattendedSlots = value;
        } else {
            payload.nonProductionSlots = value;
        }
        this._machineService.patch(this.context.data.id!, payload)
            .pipe(
                finalize(
                    () => this.loading$.next(false),
                ),
            )
            .subscribe(() => {
                this._dialogRef.close({
                    type: ModalResultType.Update,
                });
            });
    }
}
